import { NgTemplateOutlet } from '@angular/common';
import { type AfterViewInit, Component, computed, inject, input, signal, type WritableSignal } from '@angular/core';
import { type FormControl, type FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';

import { Locale, MaloupeEventName, MaloupeLocale, PersonType } from '@malou-io/package-utils';

import type { InformationFormData } from ':shared/interfaces';

import { HubSpotHttpService } from ':core/http-services/hubspot.http-service';
import { getCookie } from ':core/storage/cookie-manager';
import { RestaurantDiagnosticSearchAbstractComponent } from ':modules/restaurant-diagnostic-search/restaurant-diagnostic-search-abstract.component';
import { DiagnosticHeaderComponent } from ':shared/components/diagnostic-header/diagnostic-header.component';
import { InputGoogleMapsAutocompleteComponent } from ':shared/components/input-google-maps-autocomplete/input-google-maps-autocomplete.component';
import { InputInstagramSearchComponent } from ':shared/components/input-instagram-search/input-instagram-search.component';
import { ToastDuration } from ':shared/components/toast/toast-item/toast-item.component';
import { PRIVACY_POLICY_EN_URL, PRIVACY_POLICY_FR_URL, WEEKEND_PARIS_GENERAL_CONDITIONS_EN_URL } from ':shared/constants';
import { CookieKey, RequestLocalStorageKey } from ':shared/enums/storage-key.enum';
import { Illustration } from ':shared/pipes';
import { IllustrationPathResolverPipe } from ':shared/pipes/illustration-path-resolver.pipe';
import { increaseRequestCount } from ':shared/utils';
import { getUserIdentityFromLocalStorage } from ':shared/utils/manage-user-uuid';
import { customEmailValidator, customPhoneNumberValidator } from ':shared/validators';

interface InformationForm {
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    email: FormControl<string | null>;
    phoneNumber: FormControl<string | null>;
    locationCount: FormControl<number | null>;
    termsAndConditions: FormControl<boolean | null>;
    // fstec_2025 only
    weekendParisGeneralConditions: FormControl<boolean | null>;
}

@Component({
    selector: 'app-event-restaurant-diagnostic-search',
    templateUrl: './event-restaurant-diagnostic-search.component.html',
    styleUrl: './event-restaurant-diagnostic-search.component.scss',
    imports: [
        DiagnosticHeaderComponent,
        InputGoogleMapsAutocompleteComponent,
        InputInstagramSearchComponent,
        FormsModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        ReactiveFormsModule,
        TranslateModule,
        NgTemplateOutlet,
        IllustrationPathResolverPipe,
        MatCheckboxModule,
    ],
    providers: [{ provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS, useValue: { overlayPanelClass: 'custom-option-panel' } }],
})
export class EventRestaurantDiagnosticSearchComponent extends RestaurantDiagnosticSearchAbstractComponent implements AfterViewInit {
    readonly eventName = input.required<MaloupeEventName>();
    private readonly _hubSpotHttpService = inject(HubSpotHttpService);

    readonly Illustration = Illustration;

    readonly informationFormGroup: FormGroup<InformationForm> = this._formBuilder.group({
        firstName: [null as string | null, [Validators.required, Validators.minLength(2)]],
        lastName: [null as string | null, [Validators.required, Validators.minLength(2)]],
        email: [null as string | null, [Validators.required, customEmailValidator()]],
        phoneNumber: [null as string | null, [Validators.required, Validators.minLength(7), customPhoneNumberValidator()]],
        locationCount: [null as number | null, [Validators.required, Validators.min(1)]],
        termsAndConditions: [false as boolean | null, [Validators.requiredTrue]],
        // fstec_2025 only
        weekendParisGeneralConditions: [false as boolean | null, []],
    });

    readonly isLoading: WritableSignal<boolean> = signal(false);

    get firstName(): string | null | undefined {
        return this.informationFormGroup.get('firstName')?.value;
    }

    get lastName(): string | null | undefined {
        return this.informationFormGroup.get('lastName')?.value;
    }

    get phoneNumber(): string | null | undefined {
        return this.informationFormGroup.get('phoneNumber')?.value;
    }

    get email(): string | null | undefined {
        return this.informationFormGroup.get('email')?.value;
    }

    get locationCount(): number | null | undefined {
        return this.informationFormGroup.get('locationCount')?.value;
    }

    get fistNameControl(): FormControl<string | null> {
        return this.informationFormGroup.get('firstName') as FormControl<string | null>;
    }

    get lastNameControl(): FormControl<string | null> {
        return this.informationFormGroup.get('lastName') as FormControl<string | null>;
    }

    get phoneNumberControl(): FormControl<string | null> {
        return this.informationFormGroup.get('phoneNumber') as FormControl<string | null>;
    }

    get emailControl(): FormControl<string | null> {
        return this.informationFormGroup.get('email') as FormControl<string | null>;
    }

    get locationCountControl(): FormControl<number | null> {
        return this.informationFormGroup.get('locationCount') as FormControl<number | null>;
    }

    get termsAndConditionsControl(): FormControl<boolean | null> {
        return this.informationFormGroup.get('termsAndConditions') as FormControl<boolean | null>;
    }

    get weekendParisGeneralConditionsControl(): FormControl<boolean | null> {
        return this.informationFormGroup.get('weekendParisGeneralConditions') as FormControl<boolean | null>;
    }

    readonly phonePlaceHolder = computed(() => {
        switch (this.eventName()) {
            case MaloupeEventName.NRA_2025:
                return '+1 xxx xxx xxxx';
            case MaloupeEventName.FSTEC_2025:
                return '+1 210 xxx xxxx';
            default:
                return '07 89 98 65 43';
        }
    });

    readonly subtitle = computed(() => {
        switch (this.eventName()) {
            case MaloupeEventName.FSTEC_2025:
                return this._translateService.instant(`maloupe.diagnostic_search.event.${this.eventName()}.subtitle`);
            default:
                return this._translateService.instant('maloupe.diagnostic_search.common.subtitle');
        }
    });

    readonly isFstec = computed(() => this.eventName() === MaloupeEventName.FSTEC_2025);

    ngAfterViewInit(): void {
        this._setQueryParams();
    }

    computeMyDiagnostic(): void {
        const diagnostic = this.diagnostic();
        if (!diagnostic || this._isGetPlaceDiagnosticLimitExceeded()) {
            return;
        }
        increaseRequestCount({ key: RequestLocalStorageKey.GET_PLACE_DIAGNOSTIC_REQUEST });
        if (this.informationFormGroup.valid && diagnostic) {
            this._submitForm(diagnostic.id);
        }
    }

    preventNonNumericalValue(event: KeyboardEvent): void {
        if (!event.key.match(/\s|[0-9]/)) {
            event.preventDefault();
        }
    }

    preventInvalidPhoneNumberInput(event: KeyboardEvent): void {
        const userInput = event.target as HTMLInputElement;
        const currentValue = userInput.value;

        if (this._isInvalidPhoneNumberKey(event.key, currentValue)) {
            event.preventDefault();
        }
    }

    redirectToPrivacyPolicy(): void {
        const lang = this._translateService.currentLang;
        switch (lang) {
            case Locale.EN:
                window.open(PRIVACY_POLICY_EN_URL, '_blank');
                break;
            default:
            case Locale.FR:
                window.open(PRIVACY_POLICY_FR_URL, '_blank');
                break;
        }
    }

    redirectToWeekendInParisGeneralConditions(): void {
        window.open(WEEKEND_PARIS_GENERAL_CONDITIONS_EN_URL, '_blank');
    }

    private _isInvalidPhoneNumberKey(key: string, currentValue: string): boolean {
        const isPlusSignAtStart = key === '+' && currentValue.length > 0;
        const isNotAllowedCharacter = !key.match(/[0-9\s]/) && key !== '+';
        return isPlusSignAtStart || isNotAllowedCharacter;
    }

    private _submitForm(malouDiagnosticId: string): void {
        const firstName = this.firstName;
        const lastName = this.lastName;
        const email = this.email;
        const phoneNumber = this.phoneNumber;
        const locationCount = this.locationCount;
        if (!firstName || !lastName || !email || !phoneNumber || !locationCount) {
            this._toastService.openErrorToast(
                this._translateService.instant('maloupe.diagnostic_search.event.user_info.error_fill'),
                ToastDuration.MEDIUM
            );
            return;
        }
        this.isLoading.set(true);
        const payload: Partial<InformationFormData> = {
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            companyName: this.diagnostic()?.restaurant.name,
            phoneNumber: this.phoneNumber,
            locationCount: this.locationCount.toString(),
            zipCode: this.diagnostic()?.restaurant.address.postalCode ?? '',
            personType: PersonType.OTHER,
            utm_source: this._utmSource,
            utm_medium: this._utmMedium,
            utm_campaign: this._utmCampaign,
            utm_content: 'AppMaloupe',
            malouDiagnosticIds: [malouDiagnosticId],
            language: this._getLanguage(),
            identity: getUserIdentityFromLocalStorage(),
            pageUri: window.location.href,
            hubspotutk: getCookie(CookieKey.HUBSPOT_UTK) ?? undefined,
            eventName: this.eventName(),
            ...this._getEventSpecificProperties(),
        };

        this._hubSpotHttpService.submitForm(payload).subscribe({
            next: () => {
                this.isLoading.set(false);
                this._router.navigate(['compute-diagnostics', JSON.stringify([malouDiagnosticId])], {
                    queryParams: {
                        utm_source: this._utmSource,
                        utm_medium: this._utmMedium,
                        utm_campaign: this._utmCampaign,
                        event_name: this.eventName(),
                        first_name: firstName,
                        last_name: lastName,
                        email,
                        phone_number: phoneNumber,
                        location_count: locationCount,
                    },
                });
            },
            error: () => {
                this._toastService.openErrorToast(
                    this._translateService.instant('maloupe.diagnostic_search.event.error_submit'),
                    ToastDuration.MEDIUM
                );
                this.isLoading.set(false);
            },
        });
    }

    private _getEventSpecificProperties(): Partial<InformationFormData> {
        switch (this.eventName()) {
            case MaloupeEventName.FSTEC_2025:
                return {
                    weekendParisGeneralConditions: this.weekendParisGeneralConditionsControl.value ?? false,
                };
            default:
                return {};
        }
    }

    private _getLanguage(): MaloupeLocale {
        switch (this.eventName()) {
            // We want to force the hubspot form language to English for FSTEC 2025 event
            case MaloupeEventName.FSTEC_2025:
                return MaloupeLocale.EN;
            default:
                return this._translateService.currentLang as MaloupeLocale;
        }
    }

    private _setQueryParams(): void {
        this._activatedRoute.queryParamMap.subscribe((queryParams) => {
            const firstName = queryParams.get('first_name') ?? null;
            const lastName = queryParams.get('last_name') ?? null;
            const email = queryParams.get('email') ?? null;
            const phoneNumber = queryParams.get('phone_number') ?? null;
            const locationCount = queryParams.get('location_count') ?? null;
            const locationCountNumber = locationCount ? Number.parseInt(locationCount, 10) : NaN;
            this.informationFormGroup.patchValue({
                firstName,
                lastName,
                email,
                phoneNumber,
                locationCount: Number.isNaN(locationCountNumber) ? null : locationCountNumber,
            });
        });
    }
}
